/**
 * arbi project list                   — list projects
 * arbi project create <name>          — create a new project
 * arbi project refresh <id>           — refresh project metadata
 *
 * Projects are the tenancy unit above workspaces. Until this command landed
 * the only way to discover a `projectExtId` (needed for `arbi workspace
 * create`) was indirectly via `arbi register` / `arbi agent-create`. Exposing
 * `project list` lets agents bootstrap multi-project setups themselves.
 */

import { Command } from 'commander'
import { projects as projectOps } from '@arbidocs/sdk'
import {
  runAction,
  resolveAuth,
  printTable,
  printJson,
  suggestSubcommandAndExit,
  emitListOrTable,
} from '../helpers.js'
import { success, ref } from '../output.js'

export function registerProjectCommand(program: Command): void {
  const project = program.command('project').description('Project tenancy: list, create, refresh')

  project
    .command('list')
    .description('List your projects')
    .option('--json', 'Output as JSON')
    .action((opts: { json?: boolean }) =>
      runAction(async () => {
        const { arbi } = await resolveAuth()
        const data = await projectOps.listProjects(arbi)

        if (!emitListOrTable(data, opts, 'No projects found.')) return

        printTable(
          [
            { header: 'ID', width: 18, value: (r) => r.external_id as string },
            { header: 'NAME', width: 30, value: (r) => (r.name as string) ?? '' },
            { header: 'PLAN', width: 14, value: (r) => (r.plan as string) ?? '' },
            { header: 'ROLE', width: 14, value: (r) => (r.role as string) ?? '' },
          ],
          data
        )
      })()
    )

  project
    .command('create <name>')
    .description('Create a new project')
    .action((name: string) =>
      runAction(async () => {
        const { arbi } = await resolveAuth()
        const data = await projectOps.createProject(arbi, name)
        // Agents extract the project ID with: grep -oE 'prj-[a-z0-9]+'
        success(`Created project: ${data.name} (${ref(data.external_id)})`)
      })()
    )

  project
    .command('refresh <id>')
    .description('Refresh a project (force plan/usage sync from billing)')
    .action((id: string) =>
      runAction(async () => {
        const { arbi } = await resolveAuth()
        await projectOps.refreshProject(arbi, id)
        success(`Refreshed project: ${ref(id)}`)
      })()
    )

  // Default action: list, with typo suggestion for unknown subcommands.
  project
    .allowUnknownOption(true)
    .allowExcessArguments(true)
    .action(async () => {
      const tail = project.args ?? []
      const subcommands = project.commands.map((c) => c.name())
      const firstNonFlag = tail.find((a) => !a.startsWith('-'))
      if (firstNonFlag && !subcommands.includes(firstNonFlag)) {
        suggestSubcommandAndExit('project', firstNonFlag, subcommands)
      }
      await project.commands.find((c) => c.name() === 'list')!.parseAsync(tail, { from: 'user' })
    })
}
